<template>
    <div class="container mt-4 mb-5">
        <div class="row">
            <div class="col-md-4">
                <div class="card hoverClick mb-4">
                    <div class="card-body text-center" @click="statusFilter = 1; fetchTickets(1); scrollTable()">
                        <div class="circle-emoji mb-3">
                            <span>🚀</span>
                        </div>
                        <h1 class="card-title mb-3 fw-bold display-2 circle-emoji-shadow" style="line-height: 3rem;">{{
                            ticketTotals.opened }}</h1>
                        <p class="text-muted">Opened tickets</p>
                    </div>
                </div>
                <div class="card hoverClick mb-4">
                    <div class="card-body text-center" @click="statusFilter = 2; fetchTickets(1); scrollTable()">
                        <div class="circle-emoji mb-3">
                            <span>🧰</span>
                        </div>
                        <h1 class="card-title mb-3 fw-bold display-2 circle-emoji-shadow" style="line-height: 3rem;">{{
                            ticketTotals.in_repair }}</h1>
                        <p class="text-muted">In repair tickets</p>
                    </div>
                </div>
                <div class="card hoverClick mb-4">
                    <div class="card-body text-center" @click="statusFilter = 3; fetchTickets(1); scrollTable()">
                        <div class="circle-emoji mb-3">
                            <span>✅</span>
                        </div>
                        <h1 class="card-title mb-3 fw-bold display-2 circle-emoji-shadow" style="line-height: 3rem;">{{
                            ticketTotals.closed }}</h1>
                        <p class="text-muted">Closed tickets</p>
                    </div>
                </div>
                <div class="card mb-4">
                    <div class="card-body text-center">
                        <div class="d-flex justify-content-center" v-if="!openCamera">
                            <button
                                class="btn btn-primary mb-3 rounded-circle d-flex align-items-center justify-content-center"
                                @click="openCam"
                                style="width:86px; height:86px; font-size:0.8rem; padding:0; font-weight: 300 !important;">
                                <span class="material-icons text-white" style="font-size:2.5rem;">qr_code_scanner</span>
                            </button>
                        </div>
                        <div class="d-flex flex-column align-items-center" v-if="openCamera">
                            <qrcode-stream @decode="onDecode" @init="onInit" :key="scannerKey" />
                            <p class="text-muted mt-3">Scan a QR code</p>
                            <p class="text-muted mt-3 scanned-feedback" v-if="scanned"> ✅ Code scanned!</p>
                            <p class="serial-number mb-3" @click="openCamera=false" style="cursor:pointer;">❌ Close
                                camera</p>
                        </div>
                        <p class="text-muted" v-if="!openCamera">Click to open scan and see info from mobile on desktop
                        </p>
                    </div>
                </div>
            </div>
            <div class="col-md-8">
                <div class="card h-100">
                    <div class="card-body">
                        <h5 class="card-title mb-3">🎫 Create Inventory Ticket</h5>
                        <form @submit.prevent="createTicket">
                            <div class="row">
                                <div class="col-md-12">
                                    <div class="form-group mb-3">
                                        <label>Company</label>
                                        <multiselect v-model="selectedCompany" :options="companies" :searchable="true"
                                            :loading="isLoading" :internal-search="false" :clear-on-select="true"
                                            :close-on-select="true" :options-limit="10" :min-chars="2" label="company"
                                            track-by="id" placeholder="Search company..."
                                            @search-change="asyncFindCompany" />
                                    </div>
                                </div>
                                <div class="col-md-12 filter-row mb-3">
                                    <div class="form-group mb-3">
                                        <label>Related Job Name</label>
                                        <p>You can select a related job name from the list below:</p>
                                        <multiselect v-model="selectedJobName" :options="jobNames" :searchable="true"
                                            :loading="isLoading" :internal-search="false" :clear-on-select="true"
                                            :close-on-select="true" :options-limit="10" :min-chars="2" label="jobName"
                                            track-by="id" placeholder="Search job name..."
                                            @search-change="asyncFindJobName" @select="jumpTo('snInput')" />

                                    </div>
                                    <div class="text-center my-3">
                                        <span class="separator">
                                            <span class="separatorChild">or</span>
                                        </span>
                                    </div>
                                    <div class="form-group mb-3">
                                        <label>Job Name</label>
                                        <p>Or you can use the input field below if there are not related jobs:</p>
                                        <input type="text" class="form-control" v-model="jobName" placeholder="Job name"
                                            @focus="selectedJobName = null; jobNames = []"
                                            @input="selectedJobName = null; jobNames = []" />
                                    </div>
                                    <div class="form-group mb-3">
                                        <p v-if="selectedJobName" class="text-muted mt-3">
                                            <strong>Selected job:</strong> {{ selectedJobName.jobName }}
                                        </p>
                                    </div>
                                </div>

                                <div class="col-md-6">
                                    <div class="form-group mb-3">
                                        <label>Serial (SN)</label>
                                        <div class="input-group">
                                            <input type="text" class="form-control" v-model="sn" ref="snInput"
                                                @blur="validateSN"
                                                :class="{'is-invalid': snError, 'is-valid': snValid}" />
                                            <span class="input-group-text" v-if="snValid"><span
                                                    class="material-icons text-success">check_circle</span></span>
                                            <span class="input-group-text" v-if="snError"><span
                                                    class="material-icons text-danger">error</span></span>
                                        </div>
                                        <div v-if="snError" class="invalid-feedback d-block">{{ snErrorMsg }}</div>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="form-group mb-3">
                                        <label>Sent Date</label>
                                        <input type="datetime-local" class="form-control" v-model="sentDate" />
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="form-group mb-3">
                                        <label>Received Date</label>
                                        <input type="datetime-local" class="form-control" v-model="receivedDate" />
                                    </div>
                                </div>
                                <div class="col-md-12">
                                    <div class="form-group mb-3">
                                        <label>Sent Notes</label>
                                        <textarea class="form-control" v-model="sentNotes" maxlength="280" cols="30"
                                            rows="10"></textarea>
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-items-center gap-3">
                                <button type="submit" class="btn btn-primary">Create Event</button>
                                <p class="serial-number mb-0" @click="clearForm" style="cursor:pointer;">🔄 Reset form
                                </p>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-md-12">
                <div class="card mt-5" v-if="tickets && tickets.length">
                    <div class="card-body">
                        <h5 class="card-title mb-3">📋 Inventory Tickets</h5>

                        <div class="row mb-3 mt-3 filter-row">
                            <div class="col-md-12">
                                <p class="fw-bold">Filter</p>
                            </div>
                            <div class="col-md-12">
                                <label>Company</label>
                                <multiselect v-model="filterCompany" :options="filterCompanies" :searchable="true"
                                    :internal-search="false" :clear-on-select="true" :close-on-select="true"
                                    :options-limit="10" :min-chars="2" label="companyName" track-by="user_id"
                                    placeholder="Search company in tickets..." @search-change="asyncFindCompanyTickets"
                                    @select="fetchTickets" />

                                <label class="mt-3">Job Name</label>
                                <multiselect v-model="jobNameFilter" :options="jobNameFilterOptions" :searchable="true"
                                    :loading="jobNameFilterIsLoading" :internal-search="false" :clear-on-select="true"
                                    :close-on-select="true" :options-limit="10" :min-chars="2" label="jobNameLabel"
                                    track-by="id" placeholder="Search job name in tickets..."
                                    @search-change="asyncFindJobNameTickets" @select="searchFilterJobName" />

                                <label class="mt-3">Serial (SN)</label>
                                <multiselect v-model="snFilter" :options="snFilterOptions" :searchable="true"
                                    :loading="snFilterIsLoading" :internal-search="false" :clear-on-select="true"
                                    :close-on-select="true" :options-limit="10" :min-chars="2" label="searchName"
                                    track-by="ticketId" placeholder="Search S/N in tickets..."
                                    @search-change="asyncFindSnTickets" @select="searchFilterSn" />

                                <p class="text-muted mt-2">
                                    Type any value in the filters above and press <strong>Enter</strong> to search and
                                    narrow down the inventory tickets.
                                </p>
                            </div>
                            <div class="col-md-6" ref="mainTable">
                                <div class="btn-group w-100 mb-3" role="group" aria-label="Status filter">
                                    <button type="button" class="btn"
                                        :class="statusFilter === 0 ? 'btn-primary' : 'btn-outline-dark'"
                                        @click="statusFilter = 0; fetchTickets(1)">
                                        All
                                    </button>
                                    <button type="button" class="btn"
                                        :class="{'btn-primary': statusFilter === 1, 'btn-outline-dark': statusFilter !== 1}"
                                        @click="statusFilter = 1; fetchTickets(1)">
                                        🚀 Opened
                                    </button>
                                    <button type="button" class="btn"
                                        :class="{'btn-primary': statusFilter === 2, 'btn-outline-dark': statusFilter !== 2}"
                                        @click="statusFilter = 2; fetchTickets(1)">
                                        🧰 In Repair
                                    </button>
                                    <button type="button" class="btn"
                                        :class="{'btn-primary': statusFilter === 3, 'btn-outline-dark': statusFilter !== 3}"
                                        @click="statusFilter = 3; fetchTickets(1)">
                                        ✅ Closed
                                    </button>
                                </div>
                            </div>
                            <div class="col-md-3">

                            </div>
                            <div class="col-md-3"></div>
                            <div class="col-md-12 text-center mt-5">
                                <p class="serial-number" @click="clearAll">🔄 Reset filters</p>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-md-12">
                                <div class="d-flex justify-content-end mt-3">
                                    <v-pagination v-model.number="ticketsCurrentPage" :page-count="ticketsTotalPages"
                                        @input="fetchTickets" />
                                </div>
                                <div class="d-flex justify-content-between align-items-center mb-2">
                                    <div>
                                        <p>Total records: {{ totalRows }} </p>
                                    </div>
                                    <div>
                                        <p>Pages: {{ ticketsTotalPages }}</p>
                                    </div>
                                </div>
                                <vs-table>
                                    <template #thead>
                                        <vs-tr>
                                            <vs-th>Ticket ID</vs-th>
                                            <vs-th>Date</vs-th>
                                            <vs-th>Company</vs-th>
                                            <vs-th>Job Name</vs-th>
                                            <vs-th>S/N</vs-th>
                                            <vs-th>Status</vs-th>
                                            <vs-th>User</vs-th>
                                            <vs-th style="width: 150px;">Actions</vs-th>
                                        </vs-tr>
                                    </template>
                                    <template #tbody>
                                        <vs-tr v-for="(ticket, indextr) in tickets" :key="ticket.ticketId">
                                            <vs-td>
                                                <p class="serial-number mb-2" @click="viewTicket(ticket)">#{{
                                                    ticket.ticketId }}</p>
                                            </vs-td>
                                            <vs-td>
                                                <p class="mb-2">{{ ticket.sentDate ? ticket.sentDate.substring(0,
                                                    16).replace('T', ' ') : ''
                                                    }}</p>
                                            </vs-td>
                                            <vs-td>
                                                <p class="mb-2">{{ ticket.companyName }}</p>
                                            </vs-td>
                                            <vs-td>
                                                <p class="mb-2">{{ ticket.jobName }}</p>
                                            </vs-td>
                                            <vs-td>
                                                <p class="serial-number mb-2" @click="goRepairs(ticket)">
                                                    {{ ticket.sn }}
                                                </p>
                                            </vs-td>
                                            <vs-td>
                                                <template v-if="ticket.status==1">
                                                    <p class="mb-2">🚀 opened</p>
                                                </template>
                                                <template v-else-if="ticket.status==2">
                                                    <p class="mb-2">🧰 in repair</p>
                                                </template>
                                                <template v-else-if="ticket.status==3">
                                                    <p class="mb-2">✅ closed</p>
                                                </template>
                                                <template v-else>
                                                    <p class="mb-2">Unknown</p>
                                                </template>
                                            </vs-td>
                                            <vs-td>
                                                <p class="mb-2" :title="ticket.user">
                                                    {{
                                                    ticket.user
                                                    ? ticket.user.replace(/@.*/, '...')
                                                    : ''
                                                    }}
                                                </p>
                                            </vs-td>
                                            <vs-td class="actions-col ">
                                                <p class="serial-number me-3 mb-2" @click.prevent="printTicket(ticket)">
                                                    🖨️ Print</p>
                                                <p class="serial-number me-3 mb-2" @click.prevent="viewTicket(ticket)">➕
                                                    Add event</p>
                                                <p class="serial-number" @click.prevent="deleteTicket(ticket)">❌ Delete
                                                </p>
                                            </vs-td>

                                        </vs-tr>
                                    </template>
                                </vs-table>
                                <div class="d-flex justify-content-center mt-3">
                                    <v-pagination v-model.number="ticketsCurrentPage" :page-count="ticketsTotalPages"
                                        @input="fetchTickets" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal for TicketEditEvent -->
        <div class="modal fade" id="ticketEditEventModal" tabindex="-1" aria-labelledby="ticketEditEventModalLabel"
            aria-hidden="true" ref="ticketEditEventModal" data-bs-backdrop="static" data-bs-keyboard="false">
            <div class="modal-dialog modal-xl modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="ticketEditEventModalLabel">Edit Ticket Event</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" @click="cleanComponent"
                            aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <TicketEditEvent v-if="selectedTicket" ref="ticketEditEvent" :selectedTicket="selectedTicket"
                            @ticketUpdated="fetchTickets" />
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal for Delete Confirmation -->
        <div class="modal fade" id="deleteConfirmModal" tabindex="-1" aria-labelledby="deleteConfirmModalLabel"
            aria-hidden="true" ref="deleteConfirmModal" data-bs-backdrop="static" data-bs-keyboard="false">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="deleteConfirmModalLabel">⚠️ Confirm Delete</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <p>Are you sure you want to delete this ticket?</p>
                        <p v-if="selectedTicket"><strong>Ticket ID:</strong> #{{ selectedTicket.ticketId }}</p>
                        <p v-if="selectedTicket"><strong>Job Name:</strong> {{ selectedTicket.jobName }}</p>
                        <p v-if="selectedTicket"><strong>Serial:</strong> {{ selectedTicket.sn }}</p>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                        <button type="button" class="btn btn-danger" @click="confirmDelete">Delete</button>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
    import Multiselect from 'vue-multiselect'
    import axios from 'axios'
    import vPagination from 'vue-plain-pagination'
    var config = require("@/config/api.json");
    const API = config.api;
    import TicketEditEvent from "@/views/TicketEditEvent.vue";

    export default {
        name: 'Inventory',
        components: { Multiselect, vPagination, TicketEditEvent },
        data() {
            return {
                scannerKey: 0,
                scanned: false,
                device: '',
                camera: 'auto',
                qrCode: '',
                openCamera: false,
                selectedTicket: null,
                statusFilter: 0,
                // Para filtro de compañías en tickets
                filterCompanies: [],
                filterCompany: null,
                filterIsLoading: false,
                // Para filtro de job name en tickets
                jobNameFilter: null,
                jobNameFilterOptions: [],
                jobNameFilterIsLoading: false,
                // Para filtro de serial number en tickets
                snFilter: null,
                snFilterOptions: [],
                snFilterIsLoading: false,

                // Para crear tickets
                companies: [],
                selectedCompany: null,
                selectedJobName: null,
                jobNames: [],
                isLoading: false,
                jobName: '',
                sentDate: '',
                receivedDate: '',
                sentNotes: '',
                sn: '',
                user: '',
                snValid: false,
                snError: false,
                snErrorMsg: '',
                // Tickets paginados
                tickets: [],
                ticketsTotalPages: 1,
                totalRows: 0,
                ticketsCurrentPage: 1,
                ticketsLoading: false,
                ticketTotals: {
                    total: 0,
                    opened: 0,
                    in_repair: 0,
                    closed: 0
                },
            }
        },
        beforeDestroy() {
            this.$socket.removeEventListener('message', this.handleSocketMessage);
        },
        async mounted() {
            this.$socket.addEventListener('message', this.handleSocketMessage);
            // Llenar this.user con el usuario auth de firebase
            try {
                const { auth } = await import("@/fb");
                const user = auth.currentUser;
                if (user) {
                    this.user = user;
                    this.sendRealTimeMessage('init', 'inventory', this.user)
                } else {
                    // Si no está disponible inmediatamente, esperar a onAuthStateChanged
                    const { onAuthStateChanged } = await import("firebase/auth");
                    await new Promise((resolve) => {
                        onAuthStateChanged(auth, (u) => {
                            if (u) {
                                this.user = u;
                                this.sendRealTimeMessage('init', 'inventory', this.user);
                                resolve();
                            }
                        });
                    });
                }
            } catch (e) {
                this.user = null;
            }
            // Cargar tickets al iniciar
            this.fetchTickets(1);
            this.fetchTicketTotals();
        },

        methods: {

            handleSocketMessage(event) {
                const data = event.data;
                if (data instanceof Blob) {
                    const reader = new FileReader();
                    reader.onload = () => {
                        try {
                            const parsed = JSON.parse(reader.result);
                            if (parsed.action === 'ticket-scan') {
                                this.handleScannedTicket(parsed.user.qrcode);
                            }
                        } catch (e) {
                            console.error('Error al parsear JSON desde Blob:', e);
                        }
                    };
                    reader.readAsText(data);
                } else {
                    try {
                        const parsed = JSON.parse(data);
                        if (parsed.action === 'ticket-scan') {
                            this.handleScannedTicket(parsed.user.qrcode);
                        }
                    } catch (e) {
                        console.error('Error al procesar mensaje WebSocket:', e);
                    }
                }
            },
            async handleScannedTicket(qrcode) {
                 if (qrcode) {
                    const ticket = this.tickets.find(r => r.ticketId === Number(qrcode));
                    await this.viewTicket(ticket);
                }
            },
            openCam() {
                this.openCamera = true
            },
            onInit(promise) {
                promise.then(() => {
                    console.log("Cámara inicializada correctamente")
                }).catch(error => {
                    console.error("Error accediendo a la cámara", error)
                })
            },
            async onDecode(content) {
                this.qrCode = content
                this.scanned = true
                console.log('user', this.user);
                const scanInfo = {
                    qrcode: this.qrCode,
                    uid: this.user && this.user.uid ? this.user.uid : null
                };
                console.log('scanInfo', scanInfo);
                this.sendRealTimeMessage('ticket-scan', 'inventory', scanInfo);
                if (this.qrCode) {
                    const ticket = this.tickets.find(r => r.ticketId === Number(this.qrCode));
                    await this.viewTicket(ticket);
                }
                setTimeout(() => {
                    this.scanned = false
                    this.qrCode = ''
                    this.scannerKey++
                }, 1000);
            },
            turnCameraOn() {
                this.camera = 'auto'
            },

            turnCameraOff() {
                this.camera = 'off'
            },
            goRepairs(ticket) {
                this.$router.push({ name: 'Repair', params: { ticketId: ticket.ticketId, sn: ticket.sn } });

            },

            async confirmDelete() {
                if (!this.selectedTicket) return;
                await axios.delete(`${API}/delete-ticket/${this.selectedTicket.ticketId}`);
                this.notify('success', 'Ticket deleted successfully');
                this.callLog(this.user.email, 'delete', 'tickets', `Deleted ticket with ID ${this.selectedTicket.ticketId}`);
                this.fetchTickets(1);
                this.selectedTicket = null;
                const modal = this.$refs.deleteConfirmModal;
                if (modal) {
                    const bsModal = bootstrap.Modal.getOrCreateInstance(modal);
                    bsModal.hide();
                }
            },

            async printTicket(ticket) {
                // Descarga automáticamente el archivo del ticket tal cual viene del servidor (ej: PNG)
                try {
                    const response = await axios.get(`${API}/tag/${ticket.ticketId}`, {
                        responseType: 'blob'
                    });
                    // Usa el tipo MIME que viene del servidor, o por defecto image/png
                    const contentType = response.headers['content-type'] || 'image/png';
                    const blob = new Blob([response.data], { type: contentType });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    // Usa la extensión correcta según el tipo MIME
                    let ext = 'png';
                    if (contentType === 'image/jpeg') ext = 'jpg';
                    else if (contentType === 'image/svg+xml') ext = 'svg';
                    else if (contentType === 'image/webp') ext = 'webp';
                    await this.callLog(this.user.email, 'print', 'ticket', `Print ticket tag for ticket ID ${ticket.ticketId}`);
                    link.download = `ticket_${ticket.ticketId}.${ext}`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                } catch (e) {
                    this.notify('error', 'Error al descargar el ticket');
                }
            },

            cleanComponent() {
                this.selectedTicket = null;
                if (this.$refs.ticketEditEvent && this.$refs.ticketEditEvent.$destroy) {
                    this.$refs.ticketEditEvent.$destroy();
                }
            },

            clearForm() {
                this.selectedCompany = null;
                this.selectedJobName = null;
                this.jobNames = [];
                this.companies = [];
                this.jobName = '';
                this.sentDate = '';
                this.receivedDate = '';
                this.sentNotes = '';
                this.sn = '';
                this.user = '';
                this.snValid = false;
                this.snError = false;
                this.snErrorMsg = '';
            },

            jumpTo(refName) {
                this.$nextTick(() => {
                    if (this.$refs[refName]) {
                        this.$refs[refName].focus();
                    }
                });
            },

            viewTicket(ticket) {
                this.selectedTicket = ticket;
                const modal = this.$refs.ticketEditEventModal;
                if (modal) {
                    // Show the Bootstrap modal
                    if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
                        const bsModal = bootstrap.Modal.getOrCreateInstance(modal);
                        setTimeout(() => {
                            bsModal.show();
                        }, 222);
                    }
                }
            },

            deleteTicket(ticket) {
                this.selectedTicket = ticket;
                const modal = this.$refs.deleteConfirmModal;
                if (modal) {
                    // Show the Bootstrap modal
                    if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
                        const bsModal = bootstrap.Modal.getOrCreateInstance(modal);
                        setTimeout(() => {
                            bsModal.show();
                        }, 222);
                    }
                }
            },

            async fetchTicketTotals() {
                try {
                    const res = await axios.get(`${API}/ticket-totals`);
                    if (res.data) {
                        this.ticketTotals = {
                            total: res.data.total || 0,
                            opened: res.data.opened || 0,
                            in_repair: res.data.in_repair || 0,
                            closed: res.data.closed || 0
                        };
                    } else {
                        this.ticketTotals = { total: 0, opened: 0, in_repair: 0, closed: 0 };
                    }
                } catch (e) {
                    this.ticketTotals = { total: 0, opened: 0, in_repair: 0, closed: 0 };
                    this.notify('error', 'Error fetching ticket totals');
                }
            },

            scrollTable() {
                const tableRef = this.$refs.mainTable;
                // If it's a Vue component, get its root element
                const el = tableRef && tableRef.$el ? tableRef.$el : tableRef;
                if (el && typeof el.scrollIntoView === 'function') {
                    el.scrollIntoView({ behavior: 'smooth' });
                }
            },

            clearAll() {
                // Limpiar filtros de búsqueda
                this.filterCompany = null;
                this.filterCompanies = [];
                this.filterIsLoading = false;

                this.jobNameFilter = null;
                this.jobNameFilterOptions = [];
                this.jobNameFilterIsLoading = false;

                this.snFilter = null;
                this.snFilterOptions = [];
                this.snFilterIsLoading = false;

                // Limpiar filtro de estado
                this.statusFilter = 0;

                // Volver a cargar los tickets
                this.fetchTickets(1);
            },
            async fetchTicketsByStatus(page = 1) {
                this.ticketsLoading = true;
                this.filterCompany = null;
                this.jobNameFilter = null;
                this.snFilter = null;
                try {
                    const res = await axios.get(`${API}/get-tickets-main`, { params: { page, status: this.statusFilter } });
                    if (Array.isArray(res.data) && res.data.length > 1) {
                        // Hay tickets además del objeto de resumen
                        this.ticketsTotalPages = res.data[0].totalRows || 1;
                        this.totalRows = res.data[0].totalRows || 1;
                        this.tickets = res.data.slice(1);
                        this.ticketsCurrentPage = Number(page);
                    } else {
                        // this.tickets = [];
                        this.ticketsTotalPages = 1;
                        this.notify('info', 'No results for the selected filters.');
                    }
                } catch (e) {
                    // this.tickets = [];
                    this.ticketsTotalPages = 1;
                } finally {
                    this.ticketsLoading = false;
                }
            },

            async searchFilterSn() {
                if (!this.snFilter) {
                    this.snFilter = null;
                    return;
                }
                this.snFilterIsLoading = true;
                this.ticketsLoading = true;
                this.filterCompany = null;
                this.jobNameFilter = null;
                this.openLoading();
                try {
                    const res = await axios.get(`${API}/searchBySn`, { params: { ticketId: this.snFilter.ticketId } });
                    if (Array.isArray(res.data) && res.data.length > 0) {
                        this.ticketsTotalPages = res.data[0].totalRows || 1;
                        this.tickets = res.data.slice(1);
                        this.totalRows = res.data[0].totalTickets || 1;
                        this.ticketsCurrentPage = 1;
                    } else {
                        this.tickets = [];
                        this.ticketsTotalPages = 1;
                    }
                } catch (e) {
                    this.notify('error', 'Error searching serial numbers in tickets');
                    this.tickets = [];
                    this.ticketsTotalPages = 1;
                    this.closeLoading();
                } finally {
                    this.snFilterIsLoading = false;
                    this.ticketsLoading = false;
                    this.snFilter = null;
                    this.closeLoading();
                }
            },

            async searchFilterJobName() {
                if (!this.jobNameFilter) {
                    this.jobNameFilter = null;
                    return;
                }

                this.jobNameFilterIsLoading = true;
                this.ticketsLoading = true;
                this.filterCompany = null;
                this.openLoading();
                try {
                    const res = await axios.get(`${API}/searchByJobName`, { params: { id: this.jobNameFilter.ticketId } });
                    if (Array.isArray(res.data) && res.data.length > 0) {
                        this.ticketsTotalPages = res.data[0].totalRows || 1;
                        this.tickets = res.data.slice(1);
                        this.totalRows = res.data[0].totalTickets || 1;
                        this.ticketsCurrentPage = 1;
                    } else {
                        this.tickets = [];
                        this.ticketsTotalPages = 1;
                    }
                } catch (e) {
                    this.notify('error', 'Error searching job names in tickets');
                    this.tickets = [];
                    this.ticketsTotalPages = 1;
                    this.closeLoading();
                } finally {
                    this.jobNameFilterIsLoading = false;
                    this.ticketsLoading = false;
                    this.jobNameFilter = null;
                    this.closeLoading();
                }
            },

            async searchFilterCompany() {
                if (!this.filterCompany) {
                    this.filterCompany = null;
                    return;
                }
                this.filterIsLoading = true;
                this.ticketsLoading = true;
                this.openLoading();
                try {
                    const res = await axios.get(`${API}/searchByCompany`, { params: { user_id: this.filterCompany.user_id } });
                    // Simula el comportamiento de fetchTickets
                    if (Array.isArray(res.data) && res.data.length > 0) {
                        this.ticketsTotalPages = res.data[0].totalRows || 1;
                        this.tickets = res.data.slice(1);
                        this.ticketsCurrentPage = 1;
                    } else {
                        this.tickets = [];
                        this.ticketsTotalPages = 1;
                    }
                } catch (e) {
                    this.notify('error', 'Error searching companies in tickets');
                    this.tickets = [];
                    this.ticketsTotalPages = 1;
                    this.closeLoading();
                } finally {
                    this.filterIsLoading = false;
                    this.ticketsLoading = false;
                    this.filterCompany = null;
                    this.closeLoading();
                }
            },

            async asyncFindSnTickets(query) {
                if (!query || query.length < 2) {
                    this.snFilterOptions = []
                    return
                }
                this.snFilterIsLoading = true
                try {
                    const res = await axios.get(`${API}/search-sn-tickets`, { params: { sn: query } })
                    this.snFilterOptions = res.data
                } catch (e) {
                    this.notify('error', 'Error buscando serial numbers en tickets')
                } finally {
                    this.snFilterIsLoading = false
                }
            },

            async asyncFindJobNameTickets(query) {
                if (!query || query.length < 2) {
                    this.jobNameFilterOptions = []
                    return
                }
                this.jobNameFilterIsLoading = true
                try {
                    const res = await axios.get(`${API}/search-job-tickets`, { params: { job: query } })
                    this.jobNameFilterOptions = res.data

                } catch (e) {
                    this.notify('error', 'Error searching job names in tickets')
                } finally {
                    this.jobNameFilterIsLoading = false
                }
            },
            async fetchTickets(page = 1) {
                this.ticketsLoading = true;

                let params = { page };
                if (this.statusFilter !== 0) {
                    params.status = this.statusFilter;
                }
                if (this.filterCompany && this.filterCompany.user_id) {
                    params.user_id = this.filterCompany.user_id;
                }

                try {
                    const res = await axios.get(`${API}/get-tickets-main`, { params });
                    // Verifica si el primer elemento es el objeto de resumen (totalRows)
                    if (Array.isArray(res.data) && res.data.length > 1) {
                        this.ticketsTotalPages = res.data[0].totalRows || 1;
                        this.tickets = res.data.slice(1);
                        this.totalRows = res.data[0].totalTickets || 1;
                        this.ticketsCurrentPage = Number(page);
                    } else if (Array.isArray(res.data) && res.data.length === 1 && res.data[0].totalRows) {
                        // Solo hay objeto de resumen, sin tickets
                        this.ticketsTotalPages = res.data[0].totalRows || 1;
                        this.tickets = [];
                        this.ticketsCurrentPage = Number(page);
                    } else {
                        this.tickets = [];
                        this.ticketsTotalPages = 1;
                    }
                } catch (e) {
                    this.tickets = [];
                    this.ticketsTotalPages = 1;
                } finally {
                    this.ticketsLoading = false;
                }
            },

            async asyncFindCompany(query) {
                if (!query || query.length < 2) {
                    this.companies = []
                    return
                }
                this.isLoading = true
                try {
                    const res = await axios.get(`${API}/search-company`, { params: { company: query } })
                    this.companies = res.data
                } catch (e) {
                    this.notify('error', 'Error searching companies')
                } finally {
                    this.isLoading = false
                }
            },

            async asyncFindJobName(query) {
                if (!this.selectedCompany || !this.selectedCompany.id) {
                    this.jobNames = [];
                    this.notify('error', 'Please select a company first');
                    return;
                }
                if (!query || query.length < 2) {
                    this.jobNames = []
                    return
                }
                this.isLoading = true
                try {
                    const res = await axios.get(`${API}/search-job`, { params: { job: query, companyId: this.selectedCompany.id } })
                    this.jobNames = res.data
                } catch (e) {
                    this.notify('error', 'Error searching job names')
                } finally {
                    this.isLoading = false
                }
            },

            async asyncFindCompanyTickets(query) {
                if (!query || query.length < 2) {
                    this.filterCompanies = []
                    return
                }
                try {
                    const res = await axios.get(`${API}/search-company-tickets`, { params: { company: query } })
                    this.filterCompanies = res.data
                } catch (e) {
                    this.notify('error', 'Error buscando compañías en tickets')
                }
            },
            async validateSN() {
                this.snValid = false;
                this.snError = false;
                this.snErrorMsg = '';
                if (!this.sn) return;
                try {
                    const res = await axios.get(`${API}/verify-serialNumber`, { params: { sn: this.sn } });
                    if (res.data && typeof res.data.total === 'number' && res.data.total > 0) {
                        this.snError = true;
                        this.snErrorMsg = 'Serial number is already used in another inventory ticket.';
                        this.snValid = false;
                    } else {
                        this.snValid = true;
                        this.snError = false;
                        this.snErrorMsg = '';
                    }
                } catch (e) {
                    this.snError = true;
                    this.snErrorMsg = 'Error validating serial number.';
                    this.snValid = false;
                }
            },
            async createTicket() {
                var realJobName;
                var jobId = 0;
                if (!this.selectedCompany) {
                    this.notify('error', 'Please select a company')
                    return
                }
                if (this.selectedJobName && this.selectedJobName.id) {
                    realJobName = this.selectedJobName.jobName;
                    jobId = this.selectedJobName.id;
                } else {
                    realJobName = this.jobName;
                }

                if (!this.vEmpty(realJobName, 'Job Name')) return
                if (!this.vEmpty(this.sentDate, 'Sent Date')) return
                if (!this.vEmpty(this.receivedDate, 'Received Date')) return
                if (!this.vEmpty(this.sn, 'Serial')) return

                // Validar SN antes de enviar
                await this.validateSN();
                if (this.snError) {
                    this.notify('error', this.snErrorMsg || 'Serial number is already used.');
                    return;
                }

                const payload = {
                    user_id: this.selectedCompany.id,
                    companyName: this.selectedCompany.company,
                    jobName: realJobName,
                    jobId: jobId,
                    sentDate: this.sentDate,
                    receivedDate: this.receivedDate,
                    sentNotes: this.sentNotes,
                    status: 1,
                    sn: this.sn,
                    user: this.user.email,
                }

                this.openLoading()
                try {
                    const res = await axios.post(`${API}/create-ticket`, payload)
                    if (res.data && res.data.ticketId) {
                        this.notify('success', 'Ticket created successfully');
                        this.callLog(this.user.email, 'create', 'tickets', `Created ticket with ID ${res.data.ticketId}`);
                        this.fetchTickets();
                    } else {
                        this.notify('error', res.data && res.data.error ? res.data.error : 'Error creating ticket')
                        return;
                    }
                    this.clearForm();
                } catch (e) {
                    this.notify('error', 'Error creating ticket')
                } finally {
                    this.closeLoading()
                }
            }
        }
    }
</script>

<style>
    .actions-col {
        white-space: nowrap;
    }

    @media (max-width: 900px) {
        .actions-col {
            white-space: normal;
            display: flex;
            flex-direction: column;
            gap: 6px;
            align-items: flex-start;
        }
    }
</style>